import dayjs from 'dayjs';
import { omit } from 'lodash';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import { AnyObject } from 'yup';

import { IBusinessPlanEntity } from 'interfaces/businessPlan';
import { IBusinessPlanChapterStringify } from 'interfaces/businessTool';

import {
  FORM_TABLE_COLUMN_NAME,
  FORM_TABLE_FIRST_COLUMN_NAME,
} from 'components/_basic/Form/FormTableComponent/constants';
import { IPeriodNames, periodsConfig } from 'components/BusinessToolForm/constants/periods';
import { IBusinessToolForm, initialValues } from 'components/BusinessToolForm/utils/form';

export const getFormValueFromChapters = (
  chapters?: IBusinessPlanChapterStringify[],
  fallbackInitialValues: IBusinessToolForm = initialValues,
): IBusinessToolForm => {
  if (!chapters) return fallbackInitialValues;

  return chapters.reduce((prev, curr) => {
    const chapter = curr.chapter;
    const contentParsed = chapter ? JSON.parse(chapter.content) : fallbackInitialValues[curr.chapterName];

    return {
      ...prev,
      [curr.chapterName]: !isEmpty(contentParsed) ? contentParsed : fallbackInitialValues[curr.chapterName],
    };
  }, fallbackInitialValues);
};

export const getPeriodConfig = (period: IPeriodNames) => {
  return periodsConfig.find((item) => item.period === period)?.columns;
};

export const getPeriodColumnsNames = (period: IPeriodNames) => {
  return getPeriodConfig(period)?.map((periodName) => `${period}.${periodName}`);
};

export interface IRow {
  [key: string]: string;
}

export const createHeaderRow = (rows: string[], columnNamePrefix = FORM_TABLE_COLUMN_NAME): IRow => {
  return Object.fromEntries(rows.map((item, index) => [`${columnNamePrefix}${index}`, item]));
};

export const createDefaultRow = (columnLength: number, defaultValue = ''): IRow => {
  const properties: Record<string, string> = {};
  for (let i = 0; i < columnLength; i++) {
    properties[`${FORM_TABLE_COLUMN_NAME}${i}`] = defaultValue;
  }
  return properties;
};

export const createDefaultRows = (columnLength: number, rowsLength: number, defaultValue = ''): IRow[] => {
  const properties = createDefaultRow(columnLength, defaultValue);
  return Array.from({ length: rowsLength }, () => properties);
};

export const isEmptyRecursive = (value: unknown): boolean => {
  const emptyValues = [null, undefined, 0] as unknown[];
  if (emptyValues.includes(value)) return true;
  if (typeof value === 'string' && value.trim() === '') return true;
  if (Array.isArray(value)) return value.every(isEmptyRecursive);
  if (typeof value === 'object') {
    return Object.values(value as object).every(isEmptyRecursive);
  }
  return false; // for non-empty strings, numbers, etc.
};

const compareValuesForEmpty = (val1: unknown, val2: unknown) => {
  return isEmptyRecursive(val1) && isEmptyRecursive(val2);
};

export const calculateChanges = (
  initialValues: AnyObject,
  currentValues: AnyObject,
  fieldsToCheck: string[],
  fieldsToIgnore: string[],
) => {
  let totalFields = 0;
  let changedFields = 0;
  // These fields need for debug
  const totalFieldsValues: AnyObject[] = [];
  const changedFieldsValues: AnyObject[] = [];

  const isOptionalFormTableCell = (path: string, rowInitialValues?: AnyObject, cellValue?: unknown) => {
    if (!path.includes('[') || !path.includes(FORM_TABLE_COLUMN_NAME) || !rowInitialValues) {
      return false;
    }

    const rowTitle = rowInitialValues[FORM_TABLE_FIRST_COLUMN_NAME];

    return typeof rowTitle === 'string' && rowTitle.trim() !== '' && isEmptyRecursive(cellValue);
  };

  const processField = (path: string, initVal: any, currVal: any, rowInitialValues?: AnyObject) => {
    if (!fieldsToIgnore.some((field) => path.includes(field))) {
      if (isOptionalFormTableCell(path, rowInitialValues, currVal)) {
        return;
      }

      totalFields++;
      totalFieldsValues.push({ path, value: initVal });

      if (Array.isArray(initVal) && Array.isArray(currVal)) {
        const initValuesToCheck = initVal.reduce((acc, curr) => {
          const newObject = omit(curr, fieldsToIgnore);

          return [...acc, newObject];
        }, []);

        const currValuesToCheck = currVal.reduce((acc: unknown[], curr: any) => {
          const newObject = omit(curr, fieldsToIgnore);

          return [...acc, newObject];
        }, []);
        if (!compareValuesForEmpty(initValuesToCheck, currValuesToCheck)) {
          changedFields++;
          changedFieldsValues.push({ path, value: currVal });
        }
      } else if (!compareValuesForEmpty(initVal, currVal)) {
        changedFields++;
        changedFieldsValues.push({ path, value: currVal });
      }
    }
  };

  const processObjects = (init: AnyObject, curr: AnyObject, basePath: string = '', rowInitialValues?: AnyObject) => {
    init &&
      Object.keys(init).forEach((key) => {
        const newPath = basePath ? `${basePath}.${key}` : key;
        const currentRowInitialValues = rowInitialValues ?? (basePath.includes('[') ? init : undefined);

        if (typeof init[key] === 'object' && init[key] !== null && !Array.isArray(init[key]) && curr) {
          processObjects(init[key], curr[key], newPath, currentRowInitialValues);
        } else if (Array.isArray(init[key]) && curr && curr[key]?.length > 0) {
          init[key].forEach((_: unknown, index: number) => {
            const arrayPath = newPath + `[${index}]`;
            processObjects(init[key][index], curr[key][index], arrayPath, init[key][index]);
          });
        } else if (curr) {
          processField(newPath, init[key], curr[key], currentRowInitialValues);
        }
      });
  };

  if (fieldsToCheck && fieldsToCheck.length > 0) {
    fieldsToCheck.forEach((field) => {
      const initVal = get(initialValues, field);
      const currVal = get(currentValues, field);
      processField(field, initVal, currVal);
    });
  } else {
    processObjects(initialValues, currentValues);
  }

  return { totalFields, changedFields, totalFieldsValues, changedFieldsValues };
};

export const calculateFilledPercentage = (
  initialValues: AnyObject,
  currentValues: AnyObject,
  fieldsToCheck: string[] = [],
  fieldsToIgnore: string[] = [],
  log?: boolean,
) => {
  const { totalFields, changedFields, totalFieldsValues, changedFieldsValues } = calculateChanges(
    initialValues,
    currentValues,
    fieldsToCheck,
    fieldsToIgnore,
  );
  const percent = totalFields > 0 ? (changedFields / totalFields) * 100 : 0;
  // These logs help to easy debug difference between fields
  if (log && process.env.NODE_ENV === 'development') {
    // eslint-disable-next-line no-console
    console.log({
      percent,
      initialValues,
      fieldsToIgnore,
      currentValues,
      totalFieldsValues: totalFieldsValues.map((value) => value.path),
      changedFieldsValues: changedFieldsValues.map((value) => value.path),
    });
  }

  return percent;
};

export const getColorForProgress = (percent: number) => {
  if (percent === 100) {
    return 'success';
  } else if (percent >= 50) {
    return 'info';
  } else if (percent > 0) {
    return 'warning';
  } else {
    return 'inherit';
  }
};

export const wasBusinessPlanUpdate = (businessPlan: IBusinessPlanEntity) => {
  return dayjs(businessPlan.updatedAt).isAfter(dayjs(businessPlan.createdAt).add(2, 'second'));
};

export const updateObjectByPath = <T extends Record<string, any>>(content: T, path: string, value: unknown): T => {
  const keys = path.split('.');
  let current: any = content;

  for (let i = 0; i < keys.length - 1; i++) {
    if (!current[keys[i]] || typeof current[keys[i]] !== 'object') {
      return content; // Path doesn't exist, return original object
    }
    current = current[keys[i]];
  }

  const lastKey = keys[keys.length - 1];
  if (Object.prototype.hasOwnProperty.call(current, lastKey)) {
    current[lastKey] = value;
  }

  return { ...content };
};

export const getValueByPath = <T extends Record<string, any>>(content: T, path: string): unknown => {
  const keys = path.split('.');
  let current: any = content;

  for (let i = 0; i < keys.length; i++) {
    if (!current || typeof current !== 'object' || !(keys[i] in current)) {
      return undefined; // Path doesn't exist
    }
    current = current[keys[i]];
  }

  return current;
};
